import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable max-len */
/* eslint-disable no-unused-vars */
import React, { useCallback, useMemo, useRef, useEffect, useState, useReducer } from 'react';
import { throttle } from 'lodash-es';
import { TitleInfo, useReady, useInitialCoordination, useCoordination, useCoordinationScopes, useCoordinationScopesBy, useComplexCoordination, useMultiCoordinationScopesNonNull, useMultiCoordinationScopesSecondaryNonNull, useComplexCoordinationSecondary, useLoaders, useMergeCoordination, useMultiObsPoints, usePointMultiObsFeatureMatrixIndices, useMultiObsSegmentations, useSegmentationMultiFeatureSelection, useSegmentationMultiObsFeatureMatrixIndices, useSegmentationMultiObsSets, useGridItemSize, useMemoCustomComparison } from '@vitessce/vit-s';
import { ViewHelpMapping, ViewType, CoordinationType, COMPONENT_COORDINATION_TYPES, } from '@vitessce/constants-internal';
import { Chip } from '@vitessce/styles';
import { mergeObsSets, getCellColors, setObsSelection } from '@vitessce/sets-utils';
import { MultiLegend } from '@vitessce/legend';
import { NeuroglancerComp } from './Neuroglancer.js';
import { useNeuroglancerViewerState, pointsHaveMatchingSegmentation } from './data-hook-ng-utils.js';
import { customIsEqualForCellColors } from './use-memo-custom-equals.js';
import { useStyles } from './styles.js';
import { quaternionToEuler, eulerToQuaternion, valueGreaterThanEpsilon, nearEq, makeVitNgZoomCalibrator, conjQuat, multiplyQuat, rad2deg, deg2rad, Q_Y_UP, applyColormap, parseAnnotationChunkSegmentsWithPositions, GREY_HEX, remapCellColors, } from './utils.js';
const VITESSCE_INTERACTION_DELAY = 50;
const INIT_VIT_ZOOM = -3.6;
const ZOOM_EPS = 1e-2;
const ROTATION_EPS = 1e-3;
const TARGET_EPS = 0.5;
const NG_ROT_COOLDOWN_MS = 120;
const MESH_LOAD_THRESHOLD = 100;
const MESH_LOADING_OVERLAY_TIMEOUT = 1500;
const GUIDE_URL = 'https://vitessce.io/docs/ng-guide/';
const MESH_OPACITY = 0.6;
const LAST_INTERACTION_SOURCE = {
    vitessce: 'vitessce',
    neuroglancer: 'neuroglancer',
};
function rgbToHex(rgb) {
    return (typeof rgb === 'string'
        ? rgb
        : `#${rgb.map(c => c.toString(16).padStart(2, '0')).join('')}`);
}
export function NeuroglancerSubscriber(props) {
    const { uuid, coordinationScopes: coordinationScopesRaw, coordinationScopesBy: coordinationScopesByRaw, closeButtonVisible, downloadButtonVisible, removeGridComponent, theme, showAxisLines = true, title = 'Spatial', subtitle = 'Powered by Neuroglancer', helpText = ViewHelpMapping.NEUROGLANCER, meshLoadProjectionScaleThreshold, 
    // Note: this is a temporary mechanism
    // to pass an initial NG camera state.
    // Ideally, all camera state should be passed via
    // the existing spatialZoom, spatialTargetX, spatialRotationOrbit, etc,
    // and then NeuroglancerSubscriber should internally convert
    // to NG-compatible values, which would eliminate the need for this.
    initialNgCameraState, } = props;
    const loaders = useLoaders();
    const mergeCoordination = useMergeCoordination();
    const { classes } = useStyles();
    const initialRotationPushedRef = useRef(false);
    const getViewProjectionMatRef = useRef(null);
    const obsIdToMeshIdRef = useRef({});
    const meshIdToCellIdRef = useRef({});
    // TODO: maynot be needed for other dataset
    const cellIdToMeshIdRef = useRef({});
    const ngRotPushAtRef = useRef(0);
    const lastInteractionSource = useRef(null);
    const applyNgUpdateTimeoutRef = useRef(null);
    const lastNgPushOrientationRef = useRef(null);
    const initialRenderCalibratorRef = useRef(null);
    const translationOffsetRef = useRef([0, 0, 0]);
    const zoomRafRef = useRef(null);
    const lastNgQuatRef = useRef([0, 0, 0, 1]);
    const lastNgScaleRef = useRef(null);
    const annotationInfoRef = useRef(null);
    const annotationTransformRef = useRef(null);
    const visibleSegmentIdsRef = useRef(null);
    const chunkCacheRef = useRef(new Map());
    const resizeObserverRef = useRef(null);
    // Track layer loading state for showing loading indicator
    const [isLayersLoaded, setIsLayersLoaded] = useState(false);
    // For overlay when meshes are loaded on demand
    const [isMeshLoading, setIsMeshLoading] = useState(false);
    const [annotationReady, setAnnotationReady] = useState(false);
    const [csvLoaded, setCsvLoaded] = useState(false);
    const updateVisibleSegmentsThrottledRef = useRef(null);
    const viewportSizeRef = useRef({ width: 0, height: 0 });
    // Acccount for possible meta-coordination.
    const coordinationScopes = useCoordinationScopes(coordinationScopesRaw);
    const coordinationScopesBy = useCoordinationScopesBy(coordinationScopes, coordinationScopesByRaw);
    const [{ dataset, obsType, spatialZoom, spatialTargetX, spatialTargetY, spatialRotationX, spatialRotationY, spatialRotationZ, spatialRotationOrbit, 
    // spatialOrbitAxis, // always along Y-axis - not used in conversion
    embeddingType: mapping, obsSetColor: cellSetColor, obsSetSelection: cellSetSelection, additionalObsSets: additionalCellSets, }, { setAdditionalObsSets: setAdditionalCellSets, setObsSetColor: setCellSetColor, setObsColorEncoding: setCellColorEncoding, setObsSetSelection: setCellSetSelection, setObsHighlight: setCellHighlight, setSpatialTargetX: setTargetX, setSpatialTargetY: setTargetY, setSpatialRotationX: setRotationX, 
    // setSpatialRotationY: setRotationY,
    // setSpatialRotationZ: setRotationZ,
    setSpatialRotationOrbit: setRotationOrbit, setSpatialZoom: setZoom, }] = useCoordination(COMPONENT_COORDINATION_TYPES[ViewType.NEUROGLANCER], coordinationScopes);
    const csvUrlRef = useRef(null);
    const csvUrl = useMemo(() => {
        if (csvUrlRef.current)
            return csvUrlRef.current;
        const obsSetsMap = loaders?.[dataset]?.loaders?.obsSets;
        if (!obsSetsMap)
            return null;
        const firstLoader = obsSetsMap.values().next().value;
        const url = firstLoader?.url ?? null;
        if (url)
            csvUrlRef.current = url;
        return url;
    }, [loaders, dataset]);
    const [ngWidth, ngHeight, containerRef] = useGridItemSize();
    const [segmentationLayerScopes, segmentationChannelScopesByLayer,] = useMultiCoordinationScopesSecondaryNonNull(CoordinationType.SEGMENTATION_CHANNEL, CoordinationType.SEGMENTATION_LAYER, coordinationScopes, coordinationScopesBy);
    const pointLayerScopes = useMultiCoordinationScopesNonNull(CoordinationType.POINT_LAYER, coordinationScopes);
    // Object keys are coordination scope names for spatialSegmentationLayer.
    const segmentationLayerCoordination = useComplexCoordination([
        CoordinationType.FILE_UID,
        CoordinationType.SEGMENTATION_CHANNEL,
        CoordinationType.SPATIAL_LAYER_VISIBLE,
        CoordinationType.SPATIAL_LAYER_OPACITY,
    ], coordinationScopes, coordinationScopesBy, CoordinationType.SEGMENTATION_LAYER);
    // Object keys are coordination scope names for spatialSegmentationChannel.
    const segmentationChannelCoordination = useComplexCoordinationSecondary([
        CoordinationType.OBS_TYPE,
        CoordinationType.SPATIAL_TARGET_C,
        CoordinationType.SPATIAL_CHANNEL_VISIBLE,
        CoordinationType.SPATIAL_CHANNEL_OPACITY,
        CoordinationType.SPATIAL_CHANNEL_COLOR,
        CoordinationType.SPATIAL_SEGMENTATION_FILLED,
        CoordinationType.SPATIAL_SEGMENTATION_STROKE_WIDTH,
        CoordinationType.OBS_COLOR_ENCODING,
        CoordinationType.FEATURE_SELECTION,
        CoordinationType.FEATURE_AGGREGATION_STRATEGY,
        CoordinationType.FEATURE_VALUE_COLORMAP,
        CoordinationType.FEATURE_VALUE_COLORMAP_RANGE,
        CoordinationType.OBS_SET_COLOR,
        CoordinationType.OBS_SET_SELECTION,
        CoordinationType.ADDITIONAL_OBS_SETS,
        CoordinationType.OBS_HIGHLIGHT,
        CoordinationType.TOOLTIPS_VISIBLE,
        CoordinationType.TOOLTIP_CROSSHAIRS_VISIBLE,
        CoordinationType.LEGEND_VISIBLE,
        CoordinationType.FEATURE_TYPE,
        CoordinationType.FEATURE_VALUE_TYPE,
    ], coordinationScopes, coordinationScopesBy, CoordinationType.SEGMENTATION_LAYER, CoordinationType.SEGMENTATION_CHANNEL);
    // Point layer
    const pointLayerCoordination = useComplexCoordination([
        CoordinationType.OBS_TYPE,
        CoordinationType.SPATIAL_LAYER_VISIBLE,
        CoordinationType.SPATIAL_LAYER_OPACITY,
        CoordinationType.OBS_COLOR_ENCODING,
        CoordinationType.FEATURE_COLOR,
        CoordinationType.FEATURE_FILTER_MODE,
        CoordinationType.FEATURE_SELECTION,
        CoordinationType.FEATURE_VALUE_COLORMAP,
        CoordinationType.FEATURE_VALUE_COLORMAP_RANGE,
        CoordinationType.SPATIAL_LAYER_COLOR,
        CoordinationType.OBS_HIGHLIGHT,
        CoordinationType.TOOLTIPS_VISIBLE,
        CoordinationType.TOOLTIP_CROSSHAIRS_VISIBLE,
        CoordinationType.LEGEND_VISIBLE,
        CoordinationType.SPATIAL_POINT_STROKE_WIDTH,
        CoordinationType.OBS_SET_COLOR,
        CoordinationType.OBS_SET_SELECTION,
        CoordinationType.ADDITIONAL_OBS_SETS,
    ], coordinationScopes, coordinationScopesBy, CoordinationType.POINT_LAYER);
    // Points data
    const [obsPointsData, obsPointsDataStatus, obsPointsUrls, obsPointsErrors] = useMultiObsPoints(coordinationScopes, coordinationScopesBy, loaders, dataset, mergeCoordination, uuid);
    const [pointMultiIndicesData, pointMultiIndicesDataStatus, pointMultiIndicesDataErrors] = usePointMultiObsFeatureMatrixIndices(coordinationScopes, coordinationScopesBy, loaders, dataset);
    // Segmentations data
    const [obsSegmentationsData, obsSegmentationsDataStatus, obsSegmentationsUrls, obsSegmentationsDataErrors] = useMultiObsSegmentations(coordinationScopes, coordinationScopesBy, loaders, dataset, mergeCoordination, uuid);
    const [obsSegmentationsSetsData, obsSegmentationsSetsDataStatus, obsSegmentationsSetsDataErrors] = useSegmentationMultiObsSets(coordinationScopes, coordinationScopesBy, loaders, dataset);
    const [segmentationMultiExpressionData, segmentationMultiLoadedFeatureSelection, segmentationMultiExpressionExtents, segmentationMultiExpressionNormData, segmentationMultiFeatureSelectionStatus, segmentationMultiFeatureSelectionErrors,] = useSegmentationMultiFeatureSelection(coordinationScopes, coordinationScopesBy, loaders, dataset);
    const [segmentationMultiIndicesData, segmentationMultiIndicesDataStatus, segmentationMultiIndicesDataErrors] = useSegmentationMultiObsFeatureMatrixIndices(coordinationScopes, coordinationScopesBy, loaders, dataset);
    const errors = [
        ...obsPointsErrors,
        ...obsSegmentationsDataErrors,
        ...obsSegmentationsSetsDataErrors,
        ...pointMultiIndicesDataErrors,
        ...segmentationMultiFeatureSelectionErrors,
        ...segmentationMultiIndicesDataErrors,
    ];
    const isReady = useReady([
        // Points
        obsPointsDataStatus,
        pointMultiIndicesDataStatus,
        // Segmentations
        obsSegmentationsDataStatus,
        obsSegmentationsSetsDataStatus,
        segmentationMultiFeatureSelectionStatus,
        segmentationMultiIndicesDataStatus,
    ]);
    const lastVitessceRotationRef = useRef({
        x: spatialRotationX,
        y: spatialRotationY,
        z: spatialRotationZ,
        orbit: spatialRotationOrbit,
    });
    // Track the last coord values we saw, and only mark "vitessce"
    // when *those* actually change. This prevents cell set renders
    // from spoofing the source.
    const prevCoordsRef = useRef({
        zoom: spatialZoom,
        rx: spatialRotationX,
        ry: spatialRotationY,
        rz: spatialRotationZ,
        orbit: spatialRotationOrbit,
        tx: spatialTargetX,
        ty: spatialTargetY,
    });
    const segmentationColorMapping = useMemoCustomComparison(() => {
        // TODO: ultimately, segmentationColorMapping becomes cellColorMapping, and makes its way into the viewerState.
        // It may make sense to merge the multiple useMemoCustomComparisons upstream of derivedViewerState into one.
        // This would complicate the comparison function, but the multiple separate useMemos are not really necessary.
        const result = {};
        segmentationLayerScopes?.forEach((layerScope) => {
            result[layerScope] = {};
            segmentationChannelScopesByLayer?.[layerScope]?.forEach((channelScope) => {
                const { obsSets: layerSets, obsIndex: layerIndexFromSets } = obsSegmentationsSetsData?.[layerScope]?.[channelScope] || {};
                const layerIndex = layerIndexFromSets ?? null;
                const { obsSetColor, obsColorEncoding, obsSetSelection, additionalObsSets, spatialChannelColor, spatialChannelOpacity, featureValueColormap, featureValueColormapRange, } = segmentationChannelCoordination[0][layerScope][channelScope];
                if (obsColorEncoding === 'spatialChannelColor') {
                    // All segments get the same static channel color
                    if (spatialChannelColor) {
                        const hex = rgbToHex(spatialChannelColor);
                        const ngCellColors = {};
                        if (layerIndex) {
                            // Has obs sets — use layerIndex for IDs
                            if (obsSetSelection?.length > 0) {
                                const mergedCellSets = mergeObsSets(layerSets, additionalObsSets);
                                const selectedIds = new Set();
                                obsSetSelection.forEach((setPath) => {
                                    const rootNode = mergedCellSets?.tree?.find(n => n.name === setPath[0]);
                                    const leafNode = setPath.length > 1
                                        ? rootNode?.children?.find(n => n.name === setPath[1])
                                        : rootNode;
                                    leafNode?.set?.forEach(([id]) => selectedIds.add(String(id)));
                                });
                                layerIndex.forEach((id) => {
                                    if (selectedIds.has(String(id))) {
                                        ngCellColors[id] = hex;
                                    }
                                });
                            }
                            else {
                                // null or empty selection - show ALL segments
                                layerIndex.forEach((id) => {
                                    ngCellColors[id] = hex;
                                });
                            }
                        }
                        // Store hex as default even if no layerIndex
                        // so applyColorsAndVisibility knows the intended color
                        // result[layerScope][channelScope] = ngCellColors;
                        // TODO: Remove remapping when Meshid/cellID mismatch is fixed
                        result[layerScope][channelScope] = remapCellColors(ngCellColors, cellIdToMeshIdRef);
                        result[layerScope].opacity = spatialChannelOpacity ?? 1.0;
                        result[layerScope].defaultColor = hex; // store default color
                    }
                }
                else if (obsColorEncoding === 'geneSelection') {
                    // For NG mesh segmentations, obsIndex comes from obsSegmentationsSetsData
                    const instanceObsIndex = obsSegmentationsSetsData?.[layerScope]?.[channelScope]?.obsIndex;
                    const matrixObsIndex = segmentationMultiIndicesData?.[layerScope]?.[channelScope]?.obsIndex;
                    const expressionData = segmentationMultiExpressionNormData?.[layerScope]?.[channelScope];
                    if (instanceObsIndex && matrixObsIndex && expressionData?.[0]) {
                        // matrixObsIndex uses 'MIS_X' format, instanceObsIndex uses 'X'
                        // Strip prefix to align the two index spaces
                        const matrixIndexMap = new Map(matrixObsIndex.map((key, i) => {
                            // Strip any non-numeric prefix (e.g. 'MIS_0' -> '0')
                            const normalizedKey = key.replace(/^.*_/, '');
                            return [normalizedKey, i];
                        }));
                        const toMatrixIndex = instanceObsIndex.map(key => matrixIndexMap.get(String(key)));
                        const [low, high] = featureValueColormapRange ?? [0, 1];
                        const ngCellColors = {};
                        instanceObsIndex.forEach((id, i) => {
                            const rowIndex = toMatrixIndex[i];
                            const rawVal = expressionData[0][rowIndex] ?? 0;
                            // Uint8Array values are 0-255, already normalized — convert to 0-1
                            const t = rawVal / 255;
                            // Apply colormapRange scaling
                            const tScaled = (t - low) / Math.max(high - low, 0.0001);
                            const tClamped = Math.max(0, Math.min(1, tScaled));
                            const color = applyColormap(featureValueColormap ?? 'viridis', tClamped);
                            ngCellColors[id] = rgbToHex(color);
                        });
                        // TODO: Remove
                        result[layerScope][channelScope] = remapCellColors(ngCellColors, cellIdToMeshIdRef);
                        // result[layerScope][channelScope] = ngCellColors;
                        result[layerScope].opacity = spatialChannelOpacity ?? 1.0;
                    }
                    else if (instanceObsIndex) {
                        // No expression data available — use default color for all segments
                        const fallbackColor = spatialChannelColor ? rgbToHex(spatialChannelColor) : GREY_HEX;
                        const ngCellColors = {};
                        instanceObsIndex.forEach((id) => {
                            ngCellColors[id] = fallbackColor;
                        });
                        // TODO: remove
                        result[layerScope][channelScope] = remapCellColors(ngCellColors, cellIdToMeshIdRef);
                        // result[layerScope][channelScope] = ngCellColors;
                        result[layerScope].opacity = spatialChannelOpacity ?? 1.0;
                        result[layerScope].defaultColor = fallbackColor;
                    }
                }
                else if (layerSets && layerIndex) {
                    // cellSetSelection encoding — color by obs set membership
                    const mergedCellSets = mergeObsSets(layerSets, additionalObsSets);
                    const cellColors = getCellColors({
                        cellSets: mergedCellSets,
                        cellSetSelection: obsSetSelection,
                        cellSetColor: obsSetColor,
                        obsIndex: layerIndex,
                        theme,
                    });
                    const ngCellColors = {};
                    // cellColors is a Map keyed by segment ID
                    cellColors.forEach((color, id) => {
                        ngCellColors[id] = rgbToHex(color);
                    });
                    // TODO: remove
                    result[layerScope][channelScope] = remapCellColors(ngCellColors, cellIdToMeshIdRef);
                    // result[layerScope][channelScope] = ngCellColors;
                    result[layerScope].opacity = spatialChannelOpacity ?? 1.0;
                }
            });
        });
        return result;
    }, {
        // The dependencies for the comparison,
        // used by the custom equality function.
        segmentationLayerScopes,
        segmentationChannelScopesByLayer,
        obsSegmentationsSetsData,
        segmentationChannelCoordination,
        theme,
        segmentationMultiExpressionNormData,
        segmentationMultiIndicesData,
        csvLoaded,
    }, customIsEqualForCellColors);
    // Obtain the Neuroglancer viewerState object.
    const initialViewerState = useNeuroglancerViewerState(theme, showAxisLines, segmentationLayerScopes, segmentationChannelScopesByLayer, segmentationLayerCoordination, segmentationChannelCoordination, obsSegmentationsUrls, obsSegmentationsData, pointLayerScopes, pointLayerCoordination, obsPointsUrls, obsPointsData, pointMultiIndicesData);
    // Counter that forces derivedViewerState to re-run when visibleSegmentIdsRef changes.
    // Since refs don't trigger re-renders, incrementing this value (used as a dep in the useMemo)
    // is the mechanism to propagate culling updates to the NG viewer state.
    const [latestViewerStateIteration, incrementLatestViewerStateIteration] = useReducer(x => x + 1, 0);
    const latestViewerStateRef = useRef({
        ...initialViewerState,
        ...(initialNgCameraState ?? {}),
    });
    // TODO: For debugging in console uncomment
    // useEffect(() => {
    //   // eslint-disable-next-line no-underscore-dangle
    //   window.__chunkCache = chunkCacheRef.current;
    // }, []);
    useEffect(() => {
        if (!csvUrl)
            return;
        fetch(csvUrl)
            .then(r => r.text())
            .then((text) => {
            const lines = text.split('\n');
            const header = lines[0].split(',');
            const meshIdIdx = header.indexOf('MeshID');
            const cellIdIdx = header.indexOf('CellID');
            const meshMap = {};
            const cellMap = {};
            lines.slice(1).forEach((l) => {
                const cols = l.split(',');
                const meshId = cols[meshIdIdx]?.trim();
                const cellId = cols[cellIdIdx]?.trim();
                if (meshId && cellId) {
                    meshMap[meshId] = cellId;
                    cellMap[cellId] = meshId; // ← add reverse map
                }
            });
            meshIdToCellIdRef.current = meshMap;
            cellIdToMeshIdRef.current = cellMap;
            setCsvLoaded(true);
            // TODO: For debugging
            // window.__meshIdToCellId = meshIdToCellIdRef.current;
            // window.__cellIdToMeshIdRef = cellIdToMeshIdRef.current
        });
    }, [csvUrl]);
    // clear cache when annotation source changes
    useEffect(() => {
        chunkCacheRef.current.clear();
    }, [obsPointsData]);
    // Keep ref in sync with latest dimensions
    useEffect(() => {
        if (ngWidth && ngHeight) {
            viewportSizeRef.current = { width: ngWidth, height: ngHeight };
        }
    }, [ngWidth, ngHeight]);
    // Fallback: directly observe the container's own size.
    // The shared/global grid-resize signal (useGridItemSize) can race with this
    // view's mount order relative to sibling views, leaving viewportSizeRef
    // stuck at {0, 0} until an unrelated resize event happens to fire again.
    // A local ResizeObserver guarantees we get real dimensions as soon as
    // this container has a layout size, regardless of that timing.
    const setContainerNode = useCallback((node) => {
        containerRef.current = node; // keep useGridItemSize's internal logic in sync
        if (resizeObserverRef.current) {
            resizeObserverRef.current.disconnect();
            resizeObserverRef.current = null;
        }
        if (node) {
            // Seed synchronously in case the first ResizeObserver callback is delayed,
            // so updateVisibleSegments doesn't wait an extra tick for real dimensions.
            const rect = node.getBoundingClientRect();
            if (rect.width && rect.height) {
                viewportSizeRef.current = { width: rect.width, height: rect.height };
            }
            const observer = new ResizeObserver((entries) => {
                const entry = entries[0];
                if (!entry)
                    return;
                const { width, height } = entry.contentRect;
                if (width && height) {
                    viewportSizeRef.current = { width, height };
                }
            });
            observer.observe(node);
            resizeObserverRef.current = observer;
        }
    }, []);
    // Core viewport culling function — determines which mesh segments are visible
    // in the current camera view and updates visibleSegmentIdsRef accordingly.
    // Note: When loading overlay stops after zooming, the meshes appears after some time as fetching takes time
    /**
     ** Following are the steps/Pseudocode that provide the on-demand-mesh-loading
        Zoom in past threshold
        updateVisibleSegments() fires (throttled 500ms)
        Fetch all chunks across all spatial levels (cached after first fetch)
        Parse binary to array of { id, x, y, z, ... } per point
        Deduplicate by id across LOD levels
        Project each centroid through NG's view-projection matrix to screen pixels
        Keep only centroids within [-margin, ngWidth+margin] × [-margin, ngHeight+margin]
        visibleSegmentIdsRef = surviving IDs
        Increment latestViewerStateIteration, derivedViewerState re-runs
        derivedViewerState puts IDs into segmentation layer's segments array
        componentDidUpdate in ReactNeuroglancer.js detects segments changed and calls restoreState({ layers })
        NG receives updated segments and fetches and renders meshes for those IDs
     */
    const updateVisibleSegments = useCallback(async () => {
        // TODO: For Debugging
        // if (window.__disableCulling) return;
        if (!annotationInfoRef.current)
            return;
        if (!annotationTransformRef.current)
            return;
        if (!segmentationLayerScopes?.length)
            return;
        if (!pointLayerScopes?.length)
            return;
        const { position, projectionScale } = latestViewerStateRef.current;
        if (!position || !projectionScale)
            return;
        // Threshold check - too zoomed out, clear segments
        const maxProjectionScale = meshLoadProjectionScaleThreshold ?? MESH_LOAD_THRESHOLD;
        if (projectionScale > maxProjectionScale) {
            if (visibleSegmentIdsRef.current?.length !== 0) {
                visibleSegmentIdsRef.current = [];
                incrementLatestViewerStateIteration();
                setIsMeshLoading(false);
            }
            return;
        }
        const { width, height } = viewportSizeRef.current;
        if (!width || !height)
            return;
        const transform = annotationTransformRef.current;
        const info = annotationInfoRef.current;
        const cellsUrl = info.url;
        // // Fetch all annotation chunks across all spatial levels
        const allLevelCoords = info.spatial.flatMap((level) => {
            const [gx, gy, gz] = level.grid_shape;
            const coords = [];
            for (let cx = 0; cx < gx; cx++) {
                for (let cy = 0; cy < gy; cy++) {
                    for (let cz = 0; cz < gz; cz++) {
                        coords.push({ level: level.key, cx, cy, cz });
                    }
                }
            }
            return coords;
        });
        const fetchChunkWithPositions = async ({ level, cx, cy, cz }) => {
            const { serializers, serializer: defaultSerializer } = annotationTransformRef.current;
            // TODO: confirm for all datasets
            // All spatial levels use serializer[0] (32-byte property block).
            // Although serializer[1] (44 bytes) exists for rank-3 spatial levels spatial1/2/3,
            // the actual chunk data was generated with 32-byte properties regardless of level.
            const serializer = serializers?.[0] ?? defaultSerializer;
            if (!serializer)
                return [];
            const cacheKey = `${cellsUrl}/${level}/${cx}_${cy}_${cz}`;
            if (chunkCacheRef.current.has(cacheKey)) {
                return chunkCacheRef.current.get(cacheKey);
            }
            try {
                const res = await fetch(cacheKey);
                if (!res.ok) {
                    chunkCacheRef.current.set(cacheKey, []);
                    return [];
                }
                const buffer = await res.arrayBuffer();
                const entries = parseAnnotationChunkSegmentsWithPositions(buffer, serializer);
                chunkCacheRef.current.set(cacheKey, entries);
                return entries;
            }
            catch (e) {
                chunkCacheRef.current.set(cacheKey, []);
                return [];
            }
        };
        try {
            const results = await Promise.all(allLevelCoords.map(fetchChunkWithPositions));
            // Deduplicate by ID across all spatial levels
            const seenIds = new Set();
            const allEntries = results.flat().filter(({ id }) => {
                if (seenIds.has(id))
                    return false;
                seenIds.add(id);
                return true;
            });
            // Build obsId → meshId lookup for hover (centroid dot hover returns indexInfo/obsId)
            const obsIdToMeshId = {};
            allEntries.forEach(({ id, obsId }) => {
                obsIdToMeshId[obsId] = id;
            });
            obsIdToMeshIdRef.current = obsIdToMeshId;
            // Get current view-projection matrix from NG panel
            const mat = getViewProjectionMatRef.current?.();
            let visibleIds;
            if (!mat) {
                // Fallback: load all if projection matrix not available
                console.warn('No viewProjectionMatrix, loading all');
                visibleIds = [...new Set(allEntries.map(({ id }) => id))];
            }
            else {
                // Extend the viewport by 50% on each side (to allow mesh-loading when panning around)
                const margin = Math.max(width, height) * 0.5;
                // Screen-space projection filter
                // Screen-space culling: project each centroid from annotation space
                // to screen pixels and keep only those within the viewport bounds.
                visibleIds = [...new Set(allEntries.filter(({ x, y, z }) => {
                        // Annotation to viewer coordinates
                        const vx = x / transform.x;
                        const vy = y / transform.y;
                        const vz = (z || 0) / transform.z;
                        // Project to clip space (column-major matrix)
                        const cx = mat[0] * vx + mat[4] * vy + mat[8] * vz + mat[12];
                        const cy = mat[1] * vx + mat[5] * vy + mat[9] * vz + mat[13];
                        const cw = mat[3] * vx + mat[7] * vy + mat[11] * vz + mat[15];
                        // Perspective divide to screen pixels
                        const screenX = ((cx / cw) + 1) * 0.5 * width;
                        const screenY = (1 - (cy / cw)) * 0.5 * height;
                        // Keep centroid only if it projects within the viewport.
                        return screenX >= -margin && screenX <= width + margin
                            && screenY >= -margin && screenY <= height + margin;
                    }).map(({ id }) => id))];
            }
            // TODO: Debugging purposes - can be removed once we settle with datasets
            // visibleSegmentIdsRef.current = visibleIds;
            // window.__visibleSegmentIds = visibleSegmentIdsRef.current;
            // TODO: ( remove - validation purposes)
            // Confirming phenotypes are correct cell types for an id - tested against csv
            // console.log('[phenotype] sample entries:',
            //   allEntries.slice(0, 50).map(e => ({
            //     id: e.id, phenotype: e.phenotype
            //   }))
            // );
            // console.log("IDs", projectionScale, visibleIds.length, visibleSegmentIdsRef.current?.length);
            // If panned into an empty area
            if (visibleIds.length === 0) {
                if (visibleSegmentIdsRef.current?.length !== 0) {
                    visibleSegmentIdsRef.current = [];
                    incrementLatestViewerStateIteration();
                }
                setIsMeshLoading(false);
                return;
            }
            const prevIds = new Set(visibleSegmentIdsRef.current ?? []);
            // Count how many IDs are new (not in previous set)
            const addedIdsCount = visibleIds.filter(id => !prevIds.has(id)).length;
            // Only show overlay if significant number of new meshes need loading
            const hasSignificantChange = addedIdsCount > 20;
            if (hasSignificantChange && visibleIds.length > 0) {
                setIsMeshLoading(true);
            }
            // To update NG state only when there is actual change in ids.
            const prevSorted = [...(visibleSegmentIdsRef.current ?? [])].sort().join(',');
            const nextSorted = [...visibleIds].sort().join(',');
            if (prevSorted !== nextSorted) {
                visibleSegmentIdsRef.current = visibleIds;
                incrementLatestViewerStateIteration();
            }
            if (hasSignificantChange) {
                setTimeout(() => setIsMeshLoading(false), MESH_LOADING_OVERLAY_TIMEOUT);
            }
        }
        catch (e) {
            console.warn('[updateVisibleSegments] error:', e);
            setIsMeshLoading(false);
        }
    }, [segmentationLayerScopes, pointLayerScopes, obsPointsData, meshLoadProjectionScaleThreshold]);
    useEffect(() => {
        updateVisibleSegmentsThrottledRef.current = throttle(updateVisibleSegments, 500);
        return () => updateVisibleSegmentsThrottledRef.current?.cancel();
    }, [updateVisibleSegments]);
    useEffect(() => {
        const prevNgCameraState = {
            position: latestViewerStateRef.current.position,
            projectionOrientation: latestViewerStateRef.current.projectionOrientation,
            projectionScale: latestViewerStateRef.current.projectionScale,
        };
        latestViewerStateRef.current = {
            ...initialViewerState,
            ...prevNgCameraState,
        };
        // Force a re-render by incrementing a piece of state.
        // This works because we have made latestViewerStateIteration
        // a dependency for derivedViewerState, triggering the useMemo downstream.
        incrementLatestViewerStateIteration();
    }, [initialViewerState]);
    // Get cells URL from obsPointsUrls
    const cellsUrl = useMemo(() => {
        const firstScope = pointLayerScopes?.[0];
        return obsPointsUrls?.[firstScope]?.[0]?.url ?? null;
    }, [pointLayerScopes, obsPointsUrls]);
    // Check whether the (first) point layer's obsType matches any segmentation channel's obsType.
    // TODO: generalize to multiple point layers?
    const hasMatchingAnnotationSource = useMemo(() => {
        if (!cellsUrl)
            return false;
        const firstPointScope = pointLayerScopes?.[0];
        return pointsHaveMatchingSegmentation({
            pointObsType: pointLayerCoordination[0]?.[firstPointScope]?.obsType,
            segmentationLayerScopes,
            segmentationChannelScopesByLayer,
            segmentationChannelCoordination,
        });
    }, [cellsUrl, pointLayerScopes, segmentationLayerScopes, segmentationChannelScopesByLayer]);
    // URL of the annotation source for the points layer.
    // To fetch cells/info and spatial chunk files for viewport culling.
    useEffect(() => {
        if (!cellsUrl)
            return;
        // Fetch annotation info
        fetch(`${cellsUrl}/info`)
            .then(r => r.json())
            .then((info) => {
            const infoWithUrl = {
                ...info,
                url: cellsUrl,
            };
            annotationInfoRef.current = infoWithUrl;
            if (annotationTransformRef.current)
                setAnnotationReady(true);
        })
            .catch(err => console.error('failed to fetch annotation info:', err));
    }, [cellsUrl]);
    // Once both annotation info and transform are available, trigger the initial
    // mesh visibility update and mark the layer as loaded.
    useEffect(() => {
        if (annotationReady) {
            // Points are loaded and showing — mark as loaded
            // Meshes will load on demand when zoomed in
            setIsLayersLoaded(true);
            updateVisibleSegments();
        }
    }, [annotationReady]);
    // Callback passed to ReactNeuroglancer when the annotation layer's first chunk loads.
    // Receives the layerToChunkTransform (for coordinate space conversion) and
    // the NG property serializer (for binary chunk parsing). Sets annotationReady to trigger initial culling.
    const onAnnotationSourceReady = useCallback((transform) => {
        annotationTransformRef.current = transform;
        if (annotationInfoRef.current)
            setAnnotationReady(true);
    }, []);
    /*
     * handleStateUpdate - Interactions from NG to Vitessce are pushed here
     */
    const handleStateUpdate = useCallback((newState) => {
        lastInteractionSource.current = LAST_INTERACTION_SOURCE.neuroglancer;
        const { projectionScale, projectionOrientation, position } = newState;
        // Set the views on first mount
        if (!initialRenderCalibratorRef.current) {
            // wait for a real scale
            if (!Number.isFinite(projectionScale) || projectionScale <= 0)
                return;
            // anchor to current Vitessce zoom
            const zRef = Number.isFinite(spatialZoom) ? spatialZoom : 0;
            initialRenderCalibratorRef.current = makeVitNgZoomCalibrator(projectionScale, zRef);
            const [px = 0, py = 0, pz = 0] = position;
            const tX = Number.isFinite(spatialTargetX) ? spatialTargetX : 0;
            const tY = Number.isFinite(spatialTargetY) ? spatialTargetY : 0;
            // TODO: translation off in the first render - turn pz to 0 if z-axis needs to be avoided
            translationOffsetRef.current = [px - tX, py - tY, pz];
            // console.log(" translationOffsetRef.current",  translationOffsetRef.current)
            const syncedZoom = initialRenderCalibratorRef.current.vitToNgZoom(INIT_VIT_ZOOM);
            latestViewerStateRef.current = {
                ...latestViewerStateRef.current,
                projectionScale: syncedZoom,
            };
            if (!Number.isFinite(spatialZoom) || Math.abs(spatialZoom - INIT_VIT_ZOOM) > ZOOM_EPS) {
                setZoom(INIT_VIT_ZOOM);
            }
            return;
        }
        // ZOOM (NG → Vitessce) — do this only after calibrator exists
        if (Number.isFinite(projectionScale) && projectionScale > 0) {
            const vitZoomFromNg = initialRenderCalibratorRef.current.ngToVitZoom(projectionScale);
            const scaleChanged = lastNgScaleRef.current == null
                || (Math.abs(projectionScale - lastNgScaleRef.current)
                    > 1e-6 * Math.max(1, projectionScale));
            if (scaleChanged && Number.isFinite(vitZoomFromNg)
                && Math.abs(vitZoomFromNg - (spatialZoom ?? 0)) > ZOOM_EPS) {
                if (zoomRafRef.current)
                    cancelAnimationFrame(zoomRafRef.current);
                zoomRafRef.current = requestAnimationFrame(() => {
                    setZoom(vitZoomFromNg);
                    zoomRafRef.current = null;
                });
                // Trigger immediate mesh update on zoom change, don't wait for throttle
                updateVisibleSegments();
            }
            // remember last NG scale
            lastNgScaleRef.current = projectionScale;
        }
        // TRANSLATION
        if (Array.isArray(position) && position.length >= 2) {
            const [px, py] = position;
            const [ox, oy] = translationOffsetRef.current;
            const tx = px - ox; // map NG → Vitessce
            const ty = py - oy;
            if (Number.isFinite(tx) && Math.abs(tx - (spatialTargetX ?? tx)) > TARGET_EPS)
                setTargetX(tx);
            if (Number.isFinite(ty) && Math.abs(ty - (spatialTargetY ?? ty)) > TARGET_EPS)
                setTargetY(ty);
        }
        // ROTATION — only when NG quat actually changes
        const quatChanged = valueGreaterThanEpsilon(projectionOrientation, lastNgQuatRef.current, ROTATION_EPS);
        if (quatChanged) {
            if (applyNgUpdateTimeoutRef.current)
                clearTimeout(applyNgUpdateTimeoutRef.current);
            lastNgPushOrientationRef.current = projectionOrientation;
            applyNgUpdateTimeoutRef.current = setTimeout(() => {
                // Remove the Y-up correction before converting to Euler for Vitessce
                const qVit = multiplyQuat(conjQuat(Q_Y_UP), projectionOrientation);
                const [pitchRad, yawRad] = quaternionToEuler(qVit); // radians
                const currPitchRad = deg2rad(spatialRotationX ?? 0);
                const currYawRad = deg2rad(spatialRotationOrbit ?? 0);
                if (Math.abs(pitchRad - currPitchRad) > ROTATION_EPS
                    || Math.abs(yawRad - currYawRad) > ROTATION_EPS) {
                    const pitchDeg = rad2deg(pitchRad);
                    const yawDeg = rad2deg(yawRad);
                    // Mark Vitessce as the source for the next derived pass
                    lastInteractionSource.current = LAST_INTERACTION_SOURCE.vitessce;
                    setRotationX(pitchDeg);
                    setRotationOrbit(yawDeg);
                    ngRotPushAtRef.current = performance.now();
                    // // Test to verify rotation from NG to Vitessce and back to NG
                    // requestAnimationFrame(() => {
                    //   requestAnimationFrame(() => {
                    //     // Recreate the Vitessce quaternion from the angles we *just set*
                    //     const qVitJustSet = eulerToQuaternion(deg2rad(pitchDeg), deg2rad(yawDeg), 0);
                    //     // Convert to NG frame (apply Y-up)
                    //     const qNgExpected = multiplyQuat(Q_Y_UP, qVitJustSet);
                    //     // What NG is currently holding (latest from ref, fallback to local)
                    //     const qNgCurrent  = latestViewerStateRef.current?.projectionOrientation
                    //  || projectionOrientation;
                    //     const dot = quatdotAbs(qNgExpected, qNgCurrent);
                    //     console.log('[POST-APPLY] |dot| =', dot.toFixed(6));
                    //   });
                    // });
                }
            }, VITESSCE_INTERACTION_DELAY);
            lastNgQuatRef.current = projectionOrientation;
        }
        latestViewerStateRef.current = {
            ...latestViewerStateRef.current,
            projectionOrientation,
            projectionScale,
            position,
        };
        updateVisibleSegmentsThrottledRef.current?.();
    }, [updateVisibleSegmentsThrottledRef]);
    const onSegmentClick = useCallback((value) => {
        // Note: this callback is no longer called by the child component.
        // Reference: https://github.com/vitessce/vitessce/pull/2439
        if (value) {
            const id = String(value);
            const selectedCellIds = [id];
            const alreadySelectedId = cellSetSelection?.flat()?.some(sel => sel.includes(id));
            // Don't create new selection from same ids
            if (alreadySelectedId) {
                return;
            }
            // TODO: update this now that we are using layer/channel-based organization of segmentations.
            // There is no more "top-level" obsSets coordination; it is only on a per-layer basis.
            // We should probably just assume the first segmentation layer/channel when updating the logic,
            // since it is not clear how we would determine which layer/channel to update if there are multiple.
            setObsSelection(selectedCellIds, additionalCellSets, cellSetColor, setCellSetSelection, setAdditionalCellSets, setCellSetColor, setCellColorEncoding, 'Selection ', `: based on selected segments ${value}`);
        }
    }, [additionalCellSets, cellSetColor, setAdditionalCellSets,
        setCellColorEncoding, setCellSetColor, setCellSetSelection,
    ]);
    // Get the ultimate cellColorMapping for each layer to pass to NeuroglancerComp as a prop.
    const cellColorMappingByLayer = useMemo(() => {
        const result = {};
        segmentationLayerScopes?.forEach((layerScope) => {
            const channelScope = segmentationChannelScopesByLayer?.[layerScope]?.[0];
            result[layerScope] = {
                colors: segmentationColorMapping?.[layerScope]?.[channelScope] ?? {},
                opacity: segmentationColorMapping?.[layerScope]?.opacity ?? 1.0,
                defaultColor: segmentationColorMapping?.[layerScope]?.defaultColor ?? null,
            };
        });
        return result;
    }, [segmentationColorMapping, segmentationLayerScopes, segmentationChannelScopesByLayer]);
    useEffect(() => {
        if (!hasMatchingAnnotationSource && isReady && !segmentationLayerScopes?.length) {
            // If no segmentation layers at all — show the loading overlay
            setIsLayersLoaded(true);
        }
    }, [hasMatchingAnnotationSource, isReady, segmentationLayerScopes]);
    // For on-demand-mesh-loading use opacity to make the centroids obvious
    // TODO: may be this should be a prop?
    const meshOpacity = hasMatchingAnnotationSource ? MESH_OPACITY : undefined;
    // TODO: try to simplify using useMemoCustomComparison?
    // This would allow us to refactor a lot of the checking-for-changes logic into a comparison function,
    // simplify some of the manual bookkeeping like with prevCoordsRef and lastInteractionSource,
    // and would allow us to potentially remove usage of some refs (e.g., latestViewerStateRef)
    // by relying on the memoization to prevent unnecessary updates.
    const derivedViewerState = useMemo(() => {
        // console.log('[derivedViewerState] iteration:', latestViewerStateIteration);
        // console.log('[derivedViewerState] visibleSegmentIdsRef:', visibleSegmentIdsRef.current?.length);
        const { current } = latestViewerStateRef;
        if (current.layers.length <= 0) {
            return current;
        }
        const { projectionScale, projectionOrientation, position } = current;
        // Did Vitessce coords change vs the *previous* render?
        const rotChangedNow = !nearEq(spatialRotationX, prevCoordsRef.current.rx, ROTATION_EPS)
            || !nearEq(spatialRotationY, prevCoordsRef.current.ry, ROTATION_EPS)
            || !nearEq(spatialRotationZ, prevCoordsRef.current.rz, ROTATION_EPS)
            || !nearEq(spatialRotationOrbit, prevCoordsRef.current.orbit, ROTATION_EPS);
        const zoomChangedNow = !nearEq(spatialZoom, prevCoordsRef.current.zoom, ROTATION_EPS);
        const transChangedNow = !nearEq(spatialTargetX, prevCoordsRef.current.tx, ROTATION_EPS)
            || !nearEq(spatialTargetY, prevCoordsRef.current.ty, ROTATION_EPS);
        let nextProjectionScale = projectionScale;
        let nextPosition = position;
        // ** --- Zoom handling --- ** //
        if (typeof spatialZoom === 'number'
            && initialRenderCalibratorRef.current
            && lastInteractionSource.current !== LAST_INTERACTION_SOURCE.neuroglancer
            && zoomChangedNow) {
            const s = initialRenderCalibratorRef.current.vitToNgZoom(spatialZoom);
            if (Number.isFinite(s) && s > 0) {
                nextProjectionScale = s;
            }
        }
        // ** --- Translation handling --- ** //
        const [ox, oy, oz] = translationOffsetRef.current;
        const [px = 0, py = 0, pz = (current.position?.[2] ?? oz)] = current.position || [];
        const hasVitessceSpatialTarget = Number.isFinite(spatialTargetX)
            && Number.isFinite(spatialTargetY);
        if (hasVitessceSpatialTarget
            && lastInteractionSource.current !== LAST_INTERACTION_SOURCE.neuroglancer
            && transChangedNow) {
            const nx = spatialTargetX + ox; // Vitessce → NG
            const ny = spatialTargetY + oy;
            if (Math.abs(nx - px) > TARGET_EPS || Math.abs(ny - py) > TARGET_EPS) {
                nextPosition = [nx, ny, pz];
            }
        }
        // ** --- Orientation/Rotation handling --- ** //
        const vitessceRotationRaw = eulerToQuaternion(deg2rad(spatialRotationX ?? 0), deg2rad(spatialRotationOrbit ?? 0), deg2rad(spatialRotationZ ?? 0));
        // Apply Y-up to have both views with same axis-direction (xy)
        const vitessceRotation = multiplyQuat(Q_Y_UP, vitessceRotationRaw);
        // // Round-trip check: NG -> Vit (remove Y-UP)
        // const qVitBack = multiplyQuat(conjQuat(Q_Y_UP), vitessceRotation);
        // const dotVitLoop = quatdotAbs(qVitBack, vitessceRotationRaw);
        // // Expect ~1 (± sign OK)
        // const fmt = (v) => Array.isArray(v) ? v.map(n => Number(n).toFixed(6)) : v;
        // console.log('[CHK Vit→NG→Vit] |dot| =', dotVitLoop.toFixed(6),
        //             ' qVitRaw=', fmt(vitessceRotationRaw),
        //             ' qVitBack=', fmt(qVitBack));
        // // Cross-view check: does the NG orientation we're about to send match our Vit -> NG?
        // const dotVsNg = quatdotAbs(vitessceRotation, projectionOrientation);
        // console.log('[CHK Vit→NG vs current NG] |dot| =', dotVsNg.toFixed(6));
        // If NG quat != Vitessce quat on first render, push Vitessce once.
        const shouldForceInitialVitPush = !initialRotationPushedRef.current
            && valueGreaterThanEpsilon(vitessceRotation, projectionOrientation, ROTATION_EPS);
        // Use explicit source if set; otherwise infer Vitessce when coords changed.
        const ngFresh = (performance.now() - (ngRotPushAtRef.current || 0)) < NG_ROT_COOLDOWN_MS;
        const changedNowOrIInitialVitPush = rotChangedNow
            || zoomChangedNow || transChangedNow || shouldForceInitialVitPush;
        const src = ngFresh ? LAST_INTERACTION_SOURCE.neuroglancer
            : (lastInteractionSource.current
                ?? (changedNowOrIInitialVitPush ? LAST_INTERACTION_SOURCE.vitessce : null));
        let nextOrientation = projectionOrientation; // start from NG's current quat
        // console.log('[ORIENT]',
        //   'srcResolved=', src,
        //   'lastSource=', lastInteractionSource.current,
        //   'dotLoop=', dotVitLoop.toFixed(6),
        //   'dotCross=', dotVsNg.toFixed(6)
        // );
        // console.log('[ORIENT Q]',
        //   'qVitRaw=', fmt(vitessceRotationRaw), // Vit frame (pre Y-up)
        //   'qVitToNg=', fmt(vitessceRotation), // NG frame (post Y-up)
        //   'qNgCurr=', fmt(projectionOrientation),
        // );
        if (src === LAST_INTERACTION_SOURCE.vitessce) {
            // Only push if Vitessce rotation actually changed since last time.
            const rotDiffers = valueGreaterThanEpsilon(vitessceRotation, projectionOrientation, ROTATION_EPS);
            if (rotDiffers) {
                nextOrientation = vitessceRotation;
                lastVitessceRotationRef.current = {
                    x: spatialRotationX,
                    y: spatialRotationY,
                    z: spatialRotationZ,
                    orbit: spatialRotationOrbit,
                };
                initialRotationPushedRef.current = true;
                // Re-anchor NG -> Vitessce translation once we commit the initial orientation,
                // the center shows a right translated image
                const [cx = 0, cy = 0, cz = (nextPosition?.[2] ?? current.position?.[2] ?? 0),] = nextPosition
                    || current.position || [];
                const tX = Number.isFinite(spatialTargetX) ? spatialTargetX : 0;
                const tY = Number.isFinite(spatialTargetY) ? spatialTargetY : 0;
                translationOffsetRef.current = [cx - tX, cy - tY, cz];
            }
            // else {
            //   // No real Vitessce rotation change → do not overwrite NG's quat.
            //   console.log('Vitessce → NG: no rotation change, keep NG quat');
            // }
            if (lastInteractionSource.current === LAST_INTERACTION_SOURCE.vitessce) {
                lastInteractionSource.current = null;
            }
        }
        else if (src === LAST_INTERACTION_SOURCE.neuroglancer) {
            nextOrientation = lastNgPushOrientationRef.current ?? projectionOrientation;
            lastInteractionSource.current = null;
        }
        const updatedLayers = current?.layers?.map((layer, idx) => {
            if (layer.type !== 'segmentation')
                return layer;
            const layerScope = segmentationLayerScopes?.find(scope => layer.name?.includes(scope));
            if (!layerScope)
                return layer;
            const layerColorMapping = cellColorMappingByLayer?.[layerScope]?.colors ?? {};
            const defaultColor = cellColorMappingByLayer?.[layerScope]?.defaultColor;
            // Determine which segment IDs to pass to NG:
            let segments = [];
            if (hasMatchingAnnotationSource) {
                // Viewport culling active — use only visible segment IDs
                segments = visibleSegmentIdsRef.current ?? [];
            }
            else if (Object.keys(layerColorMapping).length > 0) {
                // No culling — show all segments from color mapping
                segments = Object.keys(layerColorMapping);
            }
            // only include colors for visible segments
            let derivedSegmentColors = {};
            if (hasMatchingAnnotationSource && segments.length > 0) {
                segments.forEach((meshId) => {
                    const color = layerColorMapping[meshId] || defaultColor;
                    if (color)
                        derivedSegmentColors[meshId] = color;
                });
            }
            else if (!hasMatchingAnnotationSource) {
                derivedSegmentColors = layerColorMapping;
            }
            return {
                ...layer,
                segments,
                segmentColors: derivedSegmentColors,
                objectAlpha: cellColorMappingByLayer?.[layerScope]?.opacity ?? 1.0,
            };
        }) ?? [];
        const updated = {
            ...current,
            projectionScale: nextProjectionScale,
            projectionOrientation: nextOrientation,
            position: nextPosition,
            layers: updatedLayers,
        };
        latestViewerStateRef.current = updated;
        prevCoordsRef.current = {
            zoom: spatialZoom,
            rx: spatialRotationX,
            ry: spatialRotationY,
            rz: spatialRotationZ,
            orbit: spatialRotationOrbit,
            tx: spatialTargetX,
            ty: spatialTargetY,
        };
        return updated;
    }, [cellColorMappingByLayer, spatialZoom, spatialRotationX, spatialRotationY,
        spatialRotationZ, spatialTargetX, spatialTargetY, initialViewerState,
        latestViewerStateIteration, hasMatchingAnnotationSource]);
    const onSegmentHighlight = useCallback((obsId) => {
        setCellHighlight(String(obsId));
    }, [setCellHighlight]);
    const handleLayerLoadingChange = useCallback((isLoaded) => {
        if (!isLayersLoaded && isLoaded) {
            setIsLayersLoaded(true);
        }
    }, [isLayersLoaded]);
    // TODO: if all cells are deselected, a black view is shown, rather we want to show empty NG view?
    // if (!cellColorMapping || Object.keys(cellColorMapping).length === 0) {
    //   return;
    // }
    const hasLayers = derivedViewerState?.layers?.length > 0;
    // TODO: generalize to support multiple point layers.
    const showPointsLegend = !hasMatchingAnnotationSource;
    return (_jsx(TitleInfo, { title: title, info: subtitle, helpText: helpText, isSpatial: true, theme: theme, closeButtonVisible: closeButtonVisible, downloadButtonVisible: downloadButtonVisible, removeGridComponent: removeGridComponent, isReady: isReady && isLayersLoaded, errors: errors, withPadding: false, guideUrl: GUIDE_URL, children: hasLayers ? (_jsxs("div", { style: { position: 'relative', width: '100%', height: '100%' }, ref: setContainerNode, children: [_jsx("div", { style: { position: 'absolute', top: 0, right: 0, zIndex: 50 }, children: _jsx(MultiLegend, { theme: "dark", maxHeight: ngHeight, 
                        // Segmentations
                        segmentationLayerScopes: segmentationLayerScopes, segmentationLayerCoordination: segmentationLayerCoordination, segmentationChannelScopesByLayer: segmentationChannelScopesByLayer, segmentationChannelCoordination: segmentationChannelCoordination, 
                        // Points
                        pointLayerScopes: showPointsLegend ? pointLayerScopes : undefined, pointLayerCoordination: pointLayerCoordination, pointMultiIndicesData: pointMultiIndicesData }) }), isMeshLoading ? (_jsx(Chip, { label: "Loading meshes...", size: "small", color: "primary", className: classes.meshLoadingIndicator })) : null, _jsx(NeuroglancerComp, { classes: classes, onSegmentClick: onSegmentClick, onSelectHoveredCoords: onSegmentHighlight, viewerState: derivedViewerState, cellColorMapping: cellColorMappingByLayer, setViewerState: handleStateUpdate, onLayerLoadingChange: handleLayerLoadingChange, onAnnotationSourceReady: onAnnotationSourceReady, onViewerReady: (getFn) => { getViewProjectionMatRef.current = getFn; }, getMeshIdToCellId: meshId => meshIdToCellIdRef.current[meshId], cellsUrl: cellsUrl, meshOpacity: meshOpacity })] })) : null }));
}
//# sourceMappingURL=NeuroglancerSubscriber.js.map